/**
 * Fetch to our API with a client-side timeout so stalled backends do not hang the UI forever.
 * Matches nginx `proxy_*_timeout 60s` so the browser and edge give up together.
 */

export const DEFAULT_API_TIMEOUT_MS = 60_000;

/** Multipart / large payloads — allow longer than default JSON calls. */
export const UPLOAD_API_TIMEOUT_MS = 120_000;

/** Large downloads (PDF, CSV, Excel, blobs) where the server may need more than {@link DEFAULT_API_TIMEOUT_MS}. */
export const HEAVY_API_TIMEOUT_MS = 120_000;

export const API_TIMEOUT_MESSAGE =
  "Permintaan habis waktu. Server tidak merespons—coba lagi.";

export type ApiFetchInit = RequestInit & { timeoutMs?: number };

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === "AbortError") ||
    (err instanceof Error && err.name === "AbortError")
  );
}

export async function apiFetch(
  input: RequestInfo | URL,
  init?: ApiFetchInit
): Promise<Response> {
  const timeoutMs = init?.timeoutMs ?? DEFAULT_API_TIMEOUT_MS;
  const userSignal = init?.signal;
  const { timeoutMs: _timeout, signal: _user, ...rest } = init ?? {};

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const mergedSignal =
    userSignal !== undefined && typeof AbortSignal.any === "function"
      ? AbortSignal.any([userSignal, controller.signal])
      : controller.signal;

  try {
    return await fetch(input, { ...rest, signal: mergedSignal });
  } catch (err: unknown) {
    if (isAbortError(err)) {
      if (userSignal?.aborted) throw err;
      throw new Error(API_TIMEOUT_MESSAGE);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
